import type { RefObject } from "react";

/** A place the focus can go: an element, a ref, or a function; a ref and a function are read at hand-off time. */
export type FocusTarget = Element | RefObject<Element | null> | null | undefined | (() => Element | null | undefined);

function resolve(target: FocusTarget): Element | null | undefined {
  if (typeof target === "function") return target();
  return target && !("nodeType" in target) ? target.current : target;
}

/**
 * The focus never stays on what leaves (spec 07 › Movimiento, P15): when
 * `leaving` holds `document.activeElement`, the focus moves to the first
 * target that is still in the document, outside `leaving` and takes it
 * (`preventScroll`). Call it in t0, the commit where the close starts, before
 * the subtree turns `inert`: an inert subtree drops its focus to <body>.
 * Returns whether the focus moved.
 */
export function handoffFocus(leaving: Element | null | undefined, targets: readonly FocusTarget[]): boolean {
  if (!leaving || typeof document === "undefined") return false;
  const active = document.activeElement;
  if (!active || active === document.body || !leaving.contains(active)) return false;
  for (const target of targets) {
    const el = resolve(target);
    if (!el || !el.isConnected || leaving.contains(el) || typeof (el as HTMLElement).focus !== "function") continue;
    (el as HTMLElement).focus({ preventScroll: true });
    // A disabled or hidden target does not take it: try the next one.
    if (document.activeElement === el) return true;
  }
  return false;
}
